import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useLazyQuery, useMutation } from '@apollo/client';

import {
  ListTransactionsRequest,
  Transaction,
  TransactionPaidStatus,
  ListTransactionFilter,
} from 'lane-shared/domains/billingPayments/types';
import { getClient } from 'lane-shared/apollo';
import { GroupType, PaymentMethod } from 'graphql-query-contracts';
import { dates } from 'lane-shared/helpers/constants';
import { convertToUUID } from 'uuid-encoding';
import {
  dateFormatter,
  getThreeLetterTimeZoneAbbreviation,
} from 'lane-shared/helpers/formatters';
import { formatCurrency, 
  currencyToPaymentCurrency, 
  formatCurrencyWithSymbolOnly,
  formatCurrencyNoSymbol 
} from 'domains-billing-currency';
import { graphQLToCurrency } from 'domains-billing-web-mappers';
import type { PillColor } from 'lane-shared/types/general/pill';
import { hasPermission } from 'lane-shared/helpers';
import { getTimeZoneByGeoLocation } from 'async-timing';

import { PageSizeType, Sort, Table, Column } from 'design-system-web';
import Pill from 'components/general/Pill';
import { AdminPage, PageHeader } from 'components/layout';
import ChannelAdminContext from 'pages/portal/admin/channel/ChannelAdminContext';

import styles from './styles.scss';
import { PERMISSION_KEYS } from 'constants-permissions';
import { useUserDataContext } from 'lane-shared/hooks';
import {
  refundTransactionMutation,
  listTransactionsQuery,
} from 'graphql-queries';
import { exportCSV } from '../charges-invoices/helpers/exportCSV';
import { useGetTransactionTableFilters } from './hooks/useGetTransactionTableFilters';
import { SortDirectionEnum } from 'constants-activate';
import useUserLocale from 'hooks/useUserLocale';

const EXPORT_PAGE_SIZE = 10000;

export function TransactionsReports() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(50);
  const { user: loggedInUser } = useUserDataContext();
  const [refundTransaction] = useMutation(refundTransactionMutation);
  const [filters, setFilters] = useState<ListTransactionFilter>({});
  const transactionTableFilters = useGetTransactionTableFilters();
  const locale = useUserLocale();

  const DEFAULT_SORT_KEY = 'created_at';
  const DEFAULT_SORT_DIRECTION = SortDirectionEnum.Desc;

  const [sort, setSort] = useState<Sort>({
    id: DEFAULT_SORT_KEY,
    direction: DEFAULT_SORT_DIRECTION,
  });

  const paymentTypeFormatMap = {
    [PaymentMethod.PaymentMethodUnknown]: t(
      'abp.transaction.paymentType.Unknown'
    ),
    [PaymentMethod.PaymentMethodCreditCard]: t(
      'abp.transaction.paymentType.CreditCard'
    ),
    [PaymentMethod.PaymentMethodCredits]: t(
      'abp.transaction.paymentType.Credits'
    ),
    [PaymentMethod.PaymentMethodApplePay]: t(
      'abp.transaction.paymentType.ApplePay'
    ),
  };

  const statusMap: { [key: string]: [string, PillColor] } = {
    SUCCEEDED: [t('abp.transaction.status.paid'), 'green'],
    FAILED: [t('abp.transaction.status.failed'), 'red'],
    REFUND_INITIATED: [t('abp.transaction.status.refunding'), 'honey'],
    REFUND_IN_PROGRESS: [t('abp.transaction.status.refunding'), 'honey'],
    REFUND_SUCCEEDED: [t('abp.transaction.status.refunded'), 'gray'],
    REFUND_FAILED: [t('abp.transaction.status.refundFailed'), 'red'],
  };

  const { channel } = useContext(ChannelAdminContext);
  const timeZone = channel?.address?.geo
    ? getTimeZoneByGeoLocation({
      longitude: channel.address.geo[0],
      latitude: channel.address.geo[1],
    })
    : '';

  const transactionIdPrefix = 'transaction_';

  const onRefund = async (transaction: Transaction) => {
    const domainCurrency = graphQLToCurrency(transaction.currency);
    const formattedAmount = formatCurrency(domainCurrency, {
      currencyDisplay: 'code',
    })(transaction.amount);

    try {
      await window.Alert.confirm({
        title: t('abp.transaction.modal.refund.title', {
          user: transaction.paidBy,
        }),
        message: t('abp.transaction.modal.refund.message', {
          amount: formattedAmount,
        }),
        confirmText: t('abp.transaction.modal.refund.confirm'),
        cancelText: t('abp.transaction.modal.refund.cancel'),
      });
    } catch (err) {
      // FIXME: we should be logging cancels, beacuse it is indicitive of users not wanting to refund, and perhaps clicked incorrectly.
      // eslint-disable-next-line no-console
      console.log(err);

      // user cancelled
      return;
    }

    try {
      await refundTransaction({
        variables: {
          refundTransactionRequest: {
            transactionId: transaction.transactionId,
          },
        },
      });

      refetchTransactionHistory();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      window.Toast.show(t('abp.transaction.modal.refund.error'));
    }
  };

  const transactionColumns: Column<Transaction>[] = [
    {
      key: 'status',
      header: t('abp.transaction.status'),
      disableSorting: true,
      renderCell: (cell: string) => (
        <Pill
          text={statusMap[cell]?.[0] || t('abp.transaction.status.processing')}
          color={statusMap[cell]?.[1] || 'honey'}
          className={styles.Pill}
        />
      ),
      renderForCSV: (cell: string, _) =>
        statusMap[cell]?.[0] || t('abp.transaction.status.processing'),
    },
    {
      key: 'paidBy',
      header: t('abp.transaction.paidBy'),
      disableSorting: true,
    },
    { key: 'productName', header: t('abp.transaction.product') },
    {
      key: 'paymentType',
      header: t('abp.transaction.paymentType'),
      renderCell: (cell: string) => (
        <span>{paymentTypeFormatMap[cell as PaymentMethod]}</span>
      ),
      renderForCSV: (cell: string) =>
        paymentTypeFormatMap[cell as PaymentMethod],
    },
    {
      key: 'createdAt',
      header: t('abp.transaction.transactionDate'),
      renderCell: (cell: string) => (
        <span>{`${dateFormatter(
          cell,
          dates.SIMPLE_DATE_TIME,
          timeZone
        )} ${getThreeLetterTimeZoneAbbreviation(timeZone)}`}</span>
      ),
      renderForCSV: (cell: string) =>
        `${dateFormatter(
          cell,
          dates.SIMPLE_DATE_TIME,
          timeZone
        )} ${getThreeLetterTimeZoneAbbreviation(timeZone)}`,
    },
    {
      key: 'currency',
      header: t('abp.chargeBillingReports.currency'),
      renderCell: (cell: number, row) => (
        <span>
          {currencyToPaymentCurrency(graphQLToCurrency(row.currency))}
        </span>
      ),
      renderForCSV: (cell: number, row) => {
        return currencyToPaymentCurrency(graphQLToCurrency(row.currency));
      },
    },
    {
      key: 'amount',
      header: t('abp.transaction.amount'),
      renderCell: (cell: number, row) => {
        const domainCurrency = graphQLToCurrency(row.currency);

        return (
          <span>
            {formatCurrencyWithSymbolOnly(domainCurrency, cell, locale)}
          </span>
        );
      },
      renderForCSV: (cell: number, row) => {
        const domainCurrency = graphQLToCurrency(row.currency);
        const formatted = formatCurrencyNoSymbol(domainCurrency, cell, locale);

        // we replace the special narrow non-breaking space inserted by Intl.NumberFormat for parsing on windows
        return formatted.replace(/[\u00A0\u202F]/g, ' ');
      },
    },

    {
      key: 'taxAmount',
      header: t('abp.transaction.taxPaid'),
      disableSorting: true,
      renderCell: (cell: number, row: Transaction) => {
        if (row?.taxAmount == null) {
          return <span />;
        }

        const domainCurrency = graphQLToCurrency(row.currency);

        return (
          <span>
            {formatCurrencyWithSymbolOnly(domainCurrency, cell, locale)}
          </span>
        );
      },

      renderForCSV: (cell: number, row: Transaction) => {
        if (row?.taxAmount != null) {
          const domainCurrency = graphQLToCurrency(row.currency);
          const formatted = formatCurrencyNoSymbol(domainCurrency, cell, locale);

          // we replace the special narrow non-breaking space inserted by Intl.NumberFormat for parsing on windows
          return formatted.replace(/[\u00A0\u202F]/g, ' ');
        }

        return '';
      },
    },
    {
      key: 'transactionId',
      header: t('abp.transaction.transactionId'),
      disableSorting: true,
      renderCell: (cell: string) => (
        <span>{cell.replace(transactionIdPrefix, '')}</span>
      ),
      renderForCSV: cell => cell.replace(transactionIdPrefix, ''),
    },
  ];

  const rowActions = [
    {
      label: t('abp.transaction.actions.refund'),
      // Option is available to super users, or users with refund transactions permission
      isHidden: (transaction: Transaction) =>
        !(
          loggedInUser?.isSuperUser ||
          hasPermission(
            loggedInUser?.roles,
            [PERMISSION_KEYS.PERMISSION_CAN_REFUND_TRANSACTIONS],
            transaction.channelId
          )
        ),
      isDisabled: (transaction: Transaction) =>
        transaction.status !== TransactionPaidStatus.SUCCESS,
      onClick: (transaction: Transaction) => onRefund(transaction),
    },
  ];

  const [
    loadTransactionHistory,
    { data, loading, refetch: refetchTransactionHistory },
  ] = useLazyQuery(listTransactionsQuery);

  const handleKeywordFilterChange = (filter: string) => {
    setPage(0);
    setFilters(prev => {
      const clone = { ...prev };

      clone.transactionId = filter;
      clone.productName = filter;

      return clone;
    });
  };

  const handleFiltersChange = (activeFilters: any[]) => {
    const tempFilter: ListTransactionFilter = {};

    if (filters.transactionId) {
      tempFilter.transactionId = filters.transactionId;
    }

    if (filters.productName) {
      tempFilter.productName = filters.productName;
    }

    for (const activeFilter of activeFilters) {
      switch (activeFilter.id) {
        case 'createdAt':
          tempFilter.startDate = new Date(activeFilter.value.startDate);
          tempFilter.endDate = new Date(activeFilter.value.endDate);
          break;
        case 'paymentType':
          tempFilter.paymentType = activeFilter.value;
          break;
        case 'status':
          tempFilter.status = activeFilter.value;
          break;
        case 'paidBy':
          tempFilter.externalUserId = activeFilter.value.map((item: string) =>
            convertToUUID(item.slice(2))
          );
          break;
      }
    }

    setPage(0);
    setFilters(tempFilter);
  };

  useEffect(() => {
    if (channel) {
      loadTransactionHistory({
        variables: {
          listTransactionsRequest: {
            groupId: convertToUUID(channel._id),
            groupType: GroupType.GroupTypeActivateChannel,
            pagination: {
              start: page * pageSize,
              perPage: pageSize,
            },
            sortBy: {
              key: 'id' in sort ? sort.id : DEFAULT_SORT_KEY,
              dir:
                'direction' in sort ? sort.direction : DEFAULT_SORT_DIRECTION,
            },
            filters,
          } as ListTransactionsRequest,
        },
      });
    }
  }, [channel, loadTransactionHistory, page, pageSize, sort, filters]);

  const handleExportToCSV = async () => {
    if (!channel) return;

    const { data } = await getClient().query({
      query: listTransactionsQuery,
      variables: {
        listTransactionsRequest: {
          groupId: convertToUUID(channel._id),
          groupType: GroupType.GroupTypeActivateChannel,
          pagination: {
            start: 0,
            perPage: EXPORT_PAGE_SIZE,
          },
          sortBy: {
            key: 'id' in sort ? sort.id : DEFAULT_SORT_KEY,
            dir: 'direction' in sort ? sort.direction : DEFAULT_SORT_DIRECTION,
          },
          filters,
        } as ListTransactionsRequest,
      },
      fetchPolicy: 'network-only',
    });

    exportCSV(
      data?.accounts?.listTransactions?.transactions || [],
      transactionColumns,
      `TransactionReports-${new Date().toISOString()}.csv`,
      'TransactionReports'
    );
  };

  const exportOptions = [
    {
      label: t('abp.chargeBillingReports.exportCsv'),
      onClick: handleExportToCSV,
    },
  ];

  return (
    <AdminPage>
      <PageHeader
        externalPadding={[0, 0]}
        header={t('abp.transactionsAndReports.transactions')}
        headerLevel="h3"
      />
      <Table
        columns={transactionColumns}
        rowActions={rowActions}
        data={data ? data.accounts.listTransactions.transactions : []}
        isLoading={loading}
        pagination="server"
        totalRows={data ? data.accounts.listTransactions.pageInfo.total : 200}
        pageSize={pageSize}
        page={page}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortChange={setSort}
        exportOptions={exportOptions}
        filters={transactionTableFilters}
        onFilterChange={handleFiltersChange}
        hasKeywordFilter
        onKeywordFilterChange={handleKeywordFilterChange}
        keywordFilterTooltip={t('abp.transaction.searchPlaceholder')}
      />
    </AdminPage>
  );
}
